/* Reusable rejection-reason picker modal.
   Used by both the Publisher Disapproval action and the Offer Request Reject
   action so the reason vocabulary and confirmation UX stay identical. */
import React, { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, Loader2 } from "lucide-react";

export const REJECTION_REASONS = [
  { key: "invalid_traffic_proofs", label: "Invalid Traffic Proofs" },
  { key: "compliance_not_met", label: "Compliance Standards Not Met" },
  { key: "quality_mismatch", label: "Quality/Creative Mismatch" },
  { key: "duplicate_profile", label: "Duplicate Profile" },
  { key: "other", label: "Other" },
];

export default function RejectReasonModal({
  open,
  title = "Confirm rejection",
  subtitle,
  targetName,
  confirmLabel = "Confirm & Send",
  onCancel,
  onConfirm,
}) {
  const [reasonKey, setReasonKey] = useState("");
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset each time the modal opens so a previous choice doesn't leak.
  useEffect(() => {
    if (open) {
      setReasonKey("");
      setOtherText("");
      setSubmitting(false);
    }
  }, [open]);

  const isOther = reasonKey === "other";
  const canConfirm = useMemo(() => {
    if (!reasonKey) return false;
    if (isOther && !otherText.trim()) return false;
    return true;
  }, [reasonKey, isOther, otherText]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const picked = REJECTION_REASONS.find((r) => r.key === reasonKey);
    setSubmitting(true);
    try {
      await onConfirm({
        reason_key: reasonKey,
        reason_detail: isOther ? otherText.trim() : "",
        reason_label: isOther ? (otherText.trim() || "Other") : picked.label,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={submitting ? undefined : onCancel}
      data-testid="reject-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-base">{title}</h3>
              {targetName && (
                <p className="text-slate-500 text-xs mt-0.5 font-mono truncate max-w-[280px]">
                  {targetName}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-slate-400 hover:text-slate-700 p-1 disabled:opacity-50"
            data-testid="reject-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {subtitle && <p className="text-slate-600 text-sm mb-3">{subtitle}</p>}

        <label
          htmlFor="reject-reason-select"
          className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1"
        >
          Reason for decision <span className="text-red-500">*</span>
        </label>
        <select
          id="reject-reason-select"
          value={reasonKey}
          onChange={(e) => setReasonKey(e.target.value)}
          disabled={submitting}
          className="w-full px-3 py-2.5 rounded-md bg-white border border-gray-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
          data-testid="reject-reason-select"
        >
          <option value="">— Select a reason —</option>
          {REJECTION_REASONS.map((r) => (
            <option key={r.key} value={r.key} data-testid={`reject-reason-option-${r.key}`}>
              {r.label}
            </option>
          ))}
        </select>

        {isOther && (
          <div className="mt-3">
            <label
              htmlFor="reject-other-text"
              className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1"
            >
              Please explain <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reject-other-text"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={submitting}
              placeholder="Describe the reason so the publisher knows what to fix…"
              className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 resize-y"
              data-testid="reject-reason-other-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">{otherText.length}/500 characters</div>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 text-[12px] px-3 py-2">
          A branded email will be sent to the publisher explaining this decision.
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md text-slate-700 hover:bg-slate-100 text-sm font-medium disabled:opacity-50"
            data-testid="reject-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="reject-modal-confirm"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
